import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/byrd/data";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, ShieldCheck } from "lucide-react";

export default function PortalInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { acceptInvite } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/invites/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Invite invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await acceptInvite(token, pw);
      toast.success("Account activated");
      nav("/portal");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to activate");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF8F1] flex flex-col">
      <div className="max-w-6xl w-full mx-auto px-5 sm:px-8 py-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#6B6558] hover:text-[#1A1A1A]">
          <ArrowLeft size={14} /> Back to home
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-5 pb-16">
        <div className="w-full max-w-md byrd-card p-8 md:p-10" data-testid="invite-card">
          <div className="flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="Byrd & CO" className="h-12 w-auto" />
            <div>
              <div className="font-serif text-xl font-bold">Byrd &amp; CO</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">Client Portal Invite</div>
            </div>
          </div>

          {error && (
            <div className="border border-[#E38380] bg-[#FADCDA] text-[#8A1F1A] rounded-md p-4 text-sm" data-testid="invite-error">
              {error}
            </div>
          )}

          {!error && !invite && <div className="text-sm text-[#6B6558]">Loading invite…</div>}

          {invite && (
            <>
              <div className="flex items-center gap-2 text-[#245C25] byrd-chip byrd-chip-green">
                <ShieldCheck size={12} /> Valid invite
              </div>
              <h1 className="font-serif text-3xl font-bold mt-4">
                Welcome, {invite.name.split(" ")[0]}.
              </h1>
              <p className="text-sm text-[#6B6558] mt-2">
                Set a password to activate your portal for <span className="font-semibold">{invite.email}</span>.
                You&apos;ll use it any time you sign in.
              </p>

              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="text-xs uppercase font-mono tracking-widest text-[#6B6558]">Choose password</label>
                  <input
                    type="password" required value={pw} onChange={(e) => setPw(e.target.value)}
                    data-testid="invite-password"
                    className="mt-1 w-full h-11 px-3 rounded-md border border-[#E4DFD1] bg-white focus:outline-none focus:ring-2 focus:ring-[#C89434]/40 focus:border-[#C89434]"
                    placeholder="At least 6 characters"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase font-mono tracking-widest text-[#6B6558]">Confirm password</label>
                  <input
                    type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)}
                    data-testid="invite-password-confirm"
                    className="mt-1 w-full h-11 px-3 rounded-md border border-[#E4DFD1] bg-white focus:outline-none focus:ring-2 focus:ring-[#C89434]/40 focus:border-[#C89434]"
                    placeholder="Re-enter password"
                  />
                </div>
                <button type="submit" disabled={busy} data-testid="invite-submit" className="byrd-btn byrd-btn-dark w-full">
                  {busy ? "Activating…" : "Activate & Enter Portal"} <ArrowRight size={16} />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
